import React, { useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { removeFieldError } from 'apps/auth/actions/formAction';
import { MAX_AMOUNT_ISSUE, MULTIPLE_AMOUNT_ISSUE } from 'apps/auth/constants/Constants';
import { RECALCULATE } from 'apps/auth/constants/ExtraNames';
import { AMOUNT_FROM, AMOUNT_TO } from 'apps/auth/constants/fieldsName';
import { getActualMaxAmountFrom, timer } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useLanguage } from 'apps/auth/hooks/selectors';
import { useNumberSeparators } from 'apps/auth/hooks/useNumberSeparators';

import ExchangeInfoPanel from './ExchangeInfoPanel';
import { SendInfoPanel } from './SendInfoPanel';
import { CalculatorAmountBlock, CalculatorInputLabel, CalculatorInputWrapper } from '../styles';

const decimalScale = 2;

const CalculatorInputAmountPureComponent = ({ isBonus = false, isSend = true, ...props }) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const [validateAfterTimer, setValidateAfterTimer] = useState(false);
    const [showSelectedAmount, setShowSelectedAmount] = useState(true);
    const isShowErrorDetails = useRef(null);
    const amountRef = useRef(null);
    const validateOnFocusRef = useRef(true);
    const timerID = useRef(null);
    const { leftLimitAmount, userCountry, additionalAmlLimit, sendFlowV3 } = useExtra();
    const isActiveInput = __isBrowser__ && props.amountField?.name === document.activeElement?.id;
    const { thousandSeparator, decimalSeparator } = useNumberSeparators(lang, country);

    useEffect(() => {
        if (props.isFrom && !isShowErrorDetails.current && !!props.amountFromValue) {
            isShowErrorDetails.current = props.customValidate(props.amountField.value);
        }
    }, [!isShowErrorDetails.current]);

    useEffect(() => {
        if (__isBrowser__ && props.focus && amountRef.current) {
            validateOnFocusRef.current = false;
            amountRef.current.focus();
            if (props.amountField.value) {
                amountRef.current.select();
            }
        }
        return () => {
            clearTimeout(timerID.current);
        };
    }, []);

    useEffect(() => {
        if (leftLimitAmount && props.isFrom) {
            isShowErrorDetails.current = props.customValidate(props.amountField.value);
        }
    }, [leftLimitAmount]);

    useEffect(() => {
        if (!props.canMakeDraftTransaction && !props.isFrom && validateAfterTimer && leftLimitAmount && props.amountFrom.value > leftLimitAmount) {
            props.updateAmountField(props.amountTo, props.amountTo.value, true, MAX_AMOUNT_ISSUE, '');
            isShowErrorDetails.current = true;
            setValidateAfterTimer(false);
        }
    }, [props.amountFrom.value, validateAfterTimer]);

    useEffect(() => {
        if (!props.isFrom && props.amountFrom.value && validateAfterTimer && props.amountTo.value && props.amountTo.value < props.restrictionTo?.minAmount) {
            isShowErrorDetails.current = props.customValidate(props.amountTo.value);
            setValidateAfterTimer(false);
        }
    }, [props.amountTo.value, validateAfterTimer]);

    useEffect(() => {
        if (leftLimitAmount === null && props.isFrom && isBonus) {
            isShowErrorDetails.current = props.customValidate(props.amountField.value);
        }
    }, [leftLimitAmount === null]);

    useEffect(() => {
        if (props.amountField.value === undefined) {
            props.updateAmountField(props.amountField, 0, false, '', '');
        }
    }, [props.amountField.value]);

    const handlerChange = (value) => {
        if (props.googlePay) {
            let totalPriceObject = { value: value };
            window.localStorage.setItem('totalPrice', JSON.stringify(totalPriceObject));
        }

        const floatValue = value.floatValue;
        if (!props.isFrom && !props.amountFrom.isError && props.amountTo.isError && props.amountTo.error?.code === MULTIPLE_AMOUNT_ISSUE) {
            props.updateAmountField(props.amountField, floatValue, true, MULTIPLE_AMOUNT_ISSUE, '');
            isShowErrorDetails.current = true;
        } else {
            props.updateAmountField(props.amountField, floatValue, false, '', '');
        }
        props.updateFontSize?.();
        if (isActiveInput) {
            if (isBonus) {
                isShowErrorDetails.current = props.customValidate(floatValue || 0);
                return;
            }

            timerID.current = timer(
                timerID.current,
                () => {
                    isShowErrorDetails.current = props.customValidate(floatValue || 0);
                    setValidateAfterTimer(true);
                },
                2000,
            );
        }
        setShowSelectedAmount(false);
    };

    const handlerBlur = (e) => {
        const value = e.target.value;
        const field = props.amountField;
        if (props.onBlurHandler) {
            props.onBlurHandler(value, field.name, field.checkRegexp, field.mandatory);
        }
    };

    const handlerFocus = () => {
        if (showSelectedAmount && props.amountField.value) {
            amountRef.current.select();
        }
        if (validateOnFocusRef.current && props.amountField.value && Number(props.amountField.value) > 0) {
            isShowErrorDetails.current = props.customValidate(props.amountField.value);
        }
        if (!validateOnFocusRef.current) {
            validateOnFocusRef.current = true;
        }
        if ((props.amountTo.isError && props.amountTo.error?.code !== MULTIPLE_AMOUNT_ISSUE) || props.amountFrom.isError) {
            props.removeFieldError(props.formName, props.isFrom ? props.amountTo.name : props.amountFrom.name);
        }

        if (
            !props.isFrom &&
            !props.canMakeDraftTransaction &&
            props.amountFrom.value > getActualMaxAmountFrom(userCountry, leftLimitAmount, props.canMakeDraftTransaction, props.restrictionFrom)
        ) {
            props.updateAmountField(props.amountField, props.amountField.value, true, MAX_AMOUNT_ISSUE, props.limitReached, true);
            isShowErrorDetails.current = true;
        }
    };

    const decreaseAmountButtonClick = (amountForChange) => {
        if (props.amountTo.isError && props.amountTo.error?.code !== MULTIPLE_AMOUNT_ISSUE) {
            props.decreaseAmountFrom(amountForChange)?.then(() => props.updateExtraByName(RECALCULATE, true));
        } else {
            props.decreaseAmountButtonClick?.(amountForChange);
            amountRef?.current?.focus();
            validateOnFocusRef.current = false;
        }
    };

    return (
        <CalculatorAmountBlock isDouble={props.isDouble}>
            <CalculatorInputLabel size="secondary" color="secondary">
                {props.title}
            </CalculatorInputLabel>
            <CalculatorInputWrapper $isError={props.amountField.isError}>
                <NumericFormat
                    getInputRef={amountRef}
                    id={props.amountField.name}
                    maxLength="14"
                    name={props.amountField.canonicalPath ? props.amountField.canonicalPath : props.amountField.name}
                    value={props.amountField.value}
                    defaultValue={'0'}
                    type={'tel'}
                    readOnly={props.isShowSpinner}
                    style={props.customFontSize ? { fontSize: props.customFontSize } : undefined}
                    autoComplete="off"
                    allowNegative={false}
                    thousandSeparator={thousandSeparator}
                    decimalSeparator={decimalSeparator}
                    fixedDecimalScale={true}
                    decimalScale={props.amountField.value ? decimalScale : false}
                    onValueChange={handlerChange}
                    onBlur={handlerBlur}
                    onFocus={handlerFocus}
                />
            </CalculatorInputWrapper>
            {isSend ? (
                <SendInfoPanel
                    formName={props.formName}
                    isBonus={isBonus}
                    isGaFlow={props.isGaFlow}
                    currencies={props.currencies}
                    currency={props.currency}
                    ccyField={props.ccyField}
                    newBalanceText={props.newBalanceText}
                    pocketBalance={props.pocketBalance}
                    amountField={props.amountField}
                    isShowErrorDetails={isShowErrorDetails}
                    restriction={props.restriction}
                    restrictionFrom={props.restrictionFrom}
                    isFrom={props.isFrom}
                    isProfileLimits={!!(leftLimitAmount && props.restriction?.maxAmount && leftLimitAmount < props.restriction?.maxAmount)}
                    additionalAmlLimit={additionalAmlLimit}
                    onSelectCurrencyClick={props.onSelectCurrencyClick}
                    decreaseAmountButtonClick={decreaseAmountButtonClick}
                    iconName={props.iconName}
                    sendFlowV3={sendFlowV3}
                />
            ) : (
                <ExchangeInfoPanel
                    currencies={props.currencies}
                    currency={props.currency}
                    ccyField={props.ccyField}
                    newBalanceText={props.newBalanceText}
                    pocketBalance={props.pocketBalance}
                    amountField={props.amountField}
                    displayArrows={props.displayArrows}
                    restriction={props.restriction}
                    onSelectCurrencyClick={props.onSelectCurrencyClick}
                    getCurrencyIcon={props.getCurrencyIcon}
                    onIconRenderError={props.onIconRenderError}
                    lang={lang}
                    country={country}
                />
            )}
        </CalculatorAmountBlock>
    );
};

CalculatorInputAmountPureComponent.propTypes = {
    isBonus: PropTypes.bool,
    formName: PropTypes.string.isRequired,
    amountField: PropTypes.object.isRequired,
    ccyField: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
    restriction: PropTypes.object,
    currencies: PropTypes.array,
    customValidate: PropTypes.func.isRequired,
    customFontSize: PropTypes.string.isRequired,
    currency: PropTypes.object,
    isSend: PropTypes.bool.isRequired,
    decreaseAmountButtonClick: PropTypes.func,
    onSelectCurrencyClick: PropTypes.func.isRequired,
    displayArrows: PropTypes.bool,
    isFrom: PropTypes.bool,
    pocketBalance: PropTypes.number,
    isDouble: PropTypes.bool,
};

const mapStateToProps = ({ app, extra, messages, form }, ownProps) => ({
    isShowSpinner: app.isShowSpinner,
    fields: form[ownProps.formName]?.fields,
    amountFrom: form[ownProps.formName].fields[AMOUNT_FROM],
    amountTo: form[ownProps.formName].fields[AMOUNT_TO],
    amountFromValue: form[ownProps.formName].fields[AMOUNT_FROM]?.value,
    canMakeDraftTransaction: extra.canMakeDraftTransaction,
    restrictionFrom: extra.commission?.restrictionFrom,
    restrictionTo: extra.commission?.restrictionTo,
    newBalanceText: messages.pageMessages.newBalance,
    limitReached: messages.pageMessages.limitReached,
});

export const CalculatorInputAmount = connect(mapStateToProps, {
    removeFieldError,
    updateExtraByName,
})(CalculatorInputAmountPureComponent);
